import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  FormField,
  FormSelect,
} from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { useContact } from "@/api/hooks/useContacts";
import { useBookStudioSession, useDayPasses, useMemberships, useStudioOccupancy } from "@/api/hooks/useMemberships";
import { formatHours, formatTimeRange } from "./hourMath";
import { ContactPicker } from "./ContactPicker";

interface BookSessionDialogProps {
  open: boolean;
  onClose: () => void;
  resourceId: string;
  defaultDate?: string;
  /** Pre-bind to one membership (opened from a member's detail page) —
   * skips the funding-source picker entirely. */
  defaultMembershipId?: string;
  onBooked?: () => void;
}

type FundingSource = "membership" | "day_pass";

/**
 * Book a 2-hour-increment Wilder Studio session. Surfaces the server's
 * quota (409, hour-bank overdraft) and capacity (409, window full) errors
 * verbatim rather than re-deriving them client-side — this dialog's own
 * disabled-state logic is a best-effort UX nicety, never the source of
 * truth (same "server decides, client previews" discipline as
 * CreateBookingDialog.tsx).
 */
export function BookSessionDialog({
  open,
  onClose,
  resourceId,
  defaultDate,
  defaultMembershipId,
  onBooked,
}: BookSessionDialogProps) {
  const [sessionDate, setSessionDate] = useState(defaultDate ?? "");
  const [source, setSource] = useState<FundingSource>("membership");
  const [membershipId, setMembershipId] = useState(defaultMembershipId ?? "");
  const [contactId, setContactId] = useState("");
  const [contactName, setContactName] = useState("");
  const [dayPassId, setDayPassId] = useState("");
  const [startIdx, setStartIdx] = useState<number | null>(null);
  const [endIdx, setEndIdx] = useState<number | null>(null);
  // True right after a first click starts a new single-window selection —
  // the NEXT click extends it into a range. False once a range is
  // complete, so the click after that starts a fresh selection instead of
  // extending forever. See handleWindowClick below; without this flag a
  // second click can't be told apart from a third.
  const [awaitingRangeEnd, setAwaitingRangeEnd] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSessionDate(defaultDate ?? "");
      setSource("membership");
      setMembershipId(defaultMembershipId ?? "");
      setContactId("");
      setContactName("");
      setDayPassId("");
      setStartIdx(null);
      setEndIdx(null);
      setAwaitingRangeEnd(false);
      setServerError(null);
    }
  }, [open, defaultDate, defaultMembershipId]);

  function handleWindowClick(idx: number) {
    if (!awaitingRangeEnd) {
      setStartIdx(idx);
      setEndIdx(idx);
      setAwaitingRangeEnd(true);
      return;
    }
    if (startIdx !== null && idx < startIdx) {
      setStartIdx(idx);
    } else {
      setEndIdx(idx);
    }
    setAwaitingRangeEnd(false);
  }

  const { data: occupancy } = useStudioOccupancy(resourceId, sessionDate);
  const { data: activeMemberships } = useMemberships({ status: "active" });
  const { data: dayPasses } = useDayPasses(source === "day_pass" ? contactId : undefined);
  const dayPassesForDate = useMemo(
    () => (dayPasses ?? []).filter((dp) => dp.pass_date === sessionDate),
    [dayPasses, sessionDate],
  );

  const bookSession = useBookStudioSession();

  const windows = occupancy?.windows ?? [];
  const spanValid = startIdx !== null && endIdx !== null && endIdx >= startIdx;
  const hours = spanValid ? (endIdx! - startIdx! + 1) * 2 : null;
  const spanHasFullWindow =
    spanValid && windows.slice(startIdx!, endIdx! + 1).some((w) => w.seats_free <= 0);

  const canSubmit =
    !!sessionDate &&
    spanValid &&
    !spanHasFullWindow &&
    (source === "membership" ? !!membershipId : !!dayPassId) &&
    !bookSession.isPending;

  async function handleSubmit() {
    if (!canSubmit || startIdx === null || endIdx === null) return;
    setServerError(null);
    try {
      await bookSession.mutateAsync({
        resource_id: resourceId,
        session_date: sessionDate,
        start_time: windows[startIdx].start_time,
        end_time: windows[endIdx].end_time,
        membership_id: source === "membership" ? membershipId : null,
        day_pass_id: source === "day_pass" ? dayPassId : null,
      });
      toastSuccess("Studio session booked");
      onBooked?.();
      onClose();
    } catch (err) {
      const message = getErrorMessage(err);
      setServerError(message);
      toastError("Couldn't book session", message);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} ariaLabel="Book studio session">
      <DialogContent size="md">
        <DialogHeader onClose={onClose}>Book studio session</DialogHeader>
        <DialogBody className="space-y-4">
          <FormField
            label="Date"
            type="date"
            value={sessionDate}
            onChange={(e) => {
              setSessionDate(e.target.value);
              setStartIdx(null);
              setEndIdx(null);
              setAwaitingRangeEnd(false);
            }}
          />

          {!defaultMembershipId && (
            <FormSelect
              label="Funding source"
              value={source}
              onChange={(e) => {
                setSource(e.target.value as FundingSource);
                setMembershipId("");
                setDayPassId("");
              }}
            >
              <option value="membership">Member hour bank</option>
              <option value="day_pass">Day pass</option>
            </FormSelect>
          )}

          {source === "membership" ? (
            <MembershipPicker
              value={membershipId}
              onChange={setMembershipId}
              memberships={activeMemberships ?? []}
              disabled={!!defaultMembershipId}
            />
          ) : (
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-text-secondary">Contact</label>
                <ContactPicker
                  value={contactId}
                  onChange={(id, name) => {
                    setContactId(id);
                    setContactName(name);
                    setDayPassId("");
                  }}
                />
              </div>
              {contactId && sessionDate && (
                <FormSelect label="Day pass" value={dayPassId} onChange={(e) => setDayPassId(e.target.value)}>
                  <option value="">Select a day pass…</option>
                  {dayPassesForDate.map((dp) => (
                    <option key={dp.id} value={dp.id}>
                      {contactName || "Contact"} — {dp.pass_date} ({dp.source === "pack" ? "from pack" : "single purchase"})
                    </option>
                  ))}
                </FormSelect>
              )}
              {contactId && sessionDate && dayPassesForDate.length === 0 && (
                <p className="text-sm text-warning" role="alert">
                  No day pass on file for this contact on {sessionDate}. Sell one first.
                </p>
              )}
            </div>
          )}

          {sessionDate && windows.length > 0 && (
            <div>
              <label className="mb-1 block text-sm font-medium text-text-secondary">Time window(s)</label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="studio-window-picker">
                {windows.map((w, idx) => {
                  const selected = startIdx !== null && endIdx !== null && idx >= startIdx && idx <= endIdx;
                  const full = w.seats_free <= 0;
                  return (
                    <button
                      key={w.window_index}
                      type="button"
                      disabled={full}
                      onClick={() => handleWindowClick(idx)}
                      className={`rounded-md border px-2 py-2 text-xs ${
                        full
                          ? "cursor-not-allowed border-border bg-bg-hover text-text-muted line-through"
                          : selected
                            ? "border-brass bg-brass/15 text-brass"
                            : "border-border text-text-secondary hover:bg-bg-hover"
                      }`}
                      data-testid={`studio-window-${idx}`}
                    >
                      <div>{formatTimeRange(w.start_time, w.end_time)}</div>
                      <div className="mt-0.5 text-[10px]">
                        {w.seats_free}/{w.capacity} free
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-xs text-text-muted">Click a window, then an end window for a longer session.</p>
            </div>
          )}

          {hours !== null && (
            <p className="text-sm text-text-secondary" data-testid="session-hours-preview">
              Session length: <span className="font-semibold text-text-primary">{formatHours(hours)}</span>
            </p>
          )}

          {spanHasFullWindow && (
            <p className="text-sm text-danger" role="alert">
              One of the selected windows is fully booked — choose a different span.
            </p>
          )}

          {serverError && (
            <p className="text-sm text-danger" role="alert" data-testid="book-session-error">
              {serverError}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={bookSession.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit} data-testid="submit-book-session">
            {bookSession.isPending ? "Booking…" : "Book session"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MembershipPicker({
  value,
  onChange,
  memberships,
  disabled,
}: {
  value: string;
  onChange: (id: string) => void;
  memberships: Array<{ id: string; contact_id: string; tier_name: string }>;
  disabled?: boolean;
}) {
  return (
    <FormSelect label="Member" value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
      <option value="">Select a member…</option>
      {memberships.map((m) => (
        <MembershipOption key={m.id} membership={m} />
      ))}
    </FormSelect>
  );
}

function MembershipOption({ membership }: { membership: { id: string; contact_id: string; tier_name: string } }) {
  const { data: contact } = useContact(membership.contact_id);
  return (
    <option value={membership.id}>
      {contact?.name ?? "…"} ({membership.tier_name})
    </option>
  );
}
